/**
 * Player settings that outlive a session.
 *
 * Deliberately paranoid about what comes back out of storage. `localStorage` is
 * shared with every other script on the origin, survives forever, and is
 * trivially editable by hand — so the stored blob is treated as untrusted input,
 * not as a serialised object. A corrupt entry costs the player their volume
 * preference; it must never cost them a silent game or a crash on load.
 */

export const SETTINGS_VERSION = 1;

export interface AudioSettings {
  /** 0..1, applied straight to the music bus gain. */
  readonly musicVolume: number;
  /** 0..1, applied straight to the effects bus gain. */
  readonly sfxVolume: number;
}

/**
 * The subset of `Storage` this needs.
 *
 * Structural rather than `Storage` itself, so tests can hand it a plain object
 * and so a host with no storage at all is a supported case rather than a crash.
 */
export interface SettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const SETTINGS_STORAGE_KEY = 'mandalizm.settings';

export class SettingsStore {
  constructor(
    private readonly storage: SettingsStorage | null,
    private readonly key: string = SETTINGS_STORAGE_KEY,
  ) {}

  /**
   * Reads the stored settings, falling back per field.
   *
   * Every failure mode lands on the defaults rather than propagating: no
   * storage, absent key, unparseable JSON, wrong shape, wrong version, a
   * non-finite number, a number out of range. A field that survives all of that
   * is still clamped before use.
   */
  load(defaults: AudioSettings): AudioSettings {
    const raw = this.readRaw();
    if (raw === null) return defaults;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return defaults;
    }
    if (typeof parsed !== 'object' || parsed === null) return defaults;
    const record = parsed as Record<string, unknown>;
    // A blob written by a future version may mean something else entirely by
    // these names, so it is ignored rather than half-read.
    if (record['version'] !== SETTINGS_VERSION) return defaults;

    return {
      musicVolume: clampVolume(record['musicVolume'], defaults.musicVolume),
      sfxVolume: clampVolume(record['sfxVolume'], defaults.sfxVolume),
    };
  }

  /** Writes settings, clamped. A storage failure is not worth a thrown error. */
  save(settings: AudioSettings): boolean {
    if (this.storage === null) return false;
    const payload = JSON.stringify({
      version: SETTINGS_VERSION,
      musicVolume: clampVolume(settings.musicVolume, 0),
      sfxVolume: clampVolume(settings.sfxVolume, 0),
    });
    try {
      this.storage.setItem(this.key, payload);
      return true;
    } catch {
      // Private browsing and full quotas both land here. The game carries on.
      return false;
    }
  }

  clear(): void {
    try {
      this.storage?.removeItem(this.key);
    } catch {
      // Nothing to do, and nothing worth failing over.
    }
  }

  private readRaw(): string | null {
    try {
      return this.storage?.getItem(this.key) ?? null;
    } catch {
      return null;
    }
  }
}

/** Coerces anything into a usable 0..1 volume, or falls back. */
export function clampVolume(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** The browser's storage, or null where it is unavailable or blocked. */
export function browserSettingsStorage(): SettingsStorage | null {
  try {
    // Touching `localStorage` throws outright under some privacy settings, so
    // the probe has to be inside the try rather than a truthiness check.
    const probe = window.localStorage;
    const key = '__mandalizm_probe__';
    probe.setItem(key, '1');
    probe.removeItem(key);
    return probe;
  } catch {
    return null;
  }
}
